import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { LabelInput } from '@/shared/components/ui/label-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { ProjectKpi } from '@/shared/types/api';
import {
  formatNumberWithDots,
  parseFormattedNumber,
} from '@/shared/utils/format';

interface KpiOperandFieldProps {
  availableKpis: ProjectKpi[];
  operandNumber: 1 | 2;
}

export const KpiOperandField = ({
  availableKpis,
  operandNumber,
}: KpiOperandFieldProps) => {
  const { t } = useTranslation('project', {
    keyPrefix: 'KpiFormEditor',
  });
  const form = useFormContext();
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:gap-2">
      <div className="flex-1">
        <FormField
          control={form.control}
          name={`kpi_operand_${operandNumber}`}
          render={({ field }) => (
            <FormItem>
              <Select
                onValueChange={(value) => {
                  field.onChange(
                    value === 'custom' ? undefined : Number(value),
                  );
                  if (value !== 'custom') {
                    form.setValue(`custom_operand_${operandNumber}`, undefined);
                  }
                }}
                value={field.value ? field.value.toString() : 'custom'}
              >
                <FormControl>
                  <SelectTrigger isInputTrigger>
                    <SelectValue
                      asLabelInput
                      label={t(
                        operandNumber === 1
                          ? 'form_field.operand_1_label'
                          : 'form_field.operand_2_label',
                      )}
                      placeholder={t(
                        operandNumber === 1
                          ? 'form_field.operand_1_placeholder'
                          : 'form_field.operand_2_placeholder',
                      )}
                    >
                      {field.value
                        ? availableKpis?.find((kpi) => kpi.id === field.value)
                            ?.name
                        : t('form_field.custom_value')}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {availableKpis?.map((kpi) => (
                    <SelectItem key={kpi.id} value={kpi.id.toString()}>
                      {kpi.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">
                    {t('operand_options.custom')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {!form.watch(`kpi_operand_${operandNumber}`) && (
        <div className="w-full sm:w-auto sm:min-w-[200px]">
          <FormField
            control={form.control}
            name={`custom_operand_${operandNumber}`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <LabelInput
                    label={t('form_field.custom_operand.name')}
                    placeholder={t('form_field.custom_operand.placeholder')}
                    value={
                      field.value === undefined
                        ? ''
                        : formatNumberWithDots(field.value)
                    }
                    onChange={(e) => {
                      const parsed = parseFormattedNumber(e.target.value);
                      field.onChange(
                        e.target.value === '' ? undefined : parsed,
                      );
                    }}
                    onBlur={field.onBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};
